// ==========================================
// Quest rail (1c) — the sidebar layout
// ==========================================
//   ┌──┬──────────────────────────┐ 300
//   │▲ │ Daily quest        2/3   │  quest header, dismissible for the day
//   │12│ ────────────────────────  │
//   │──│                          │
//   │✉ │  ┌───────────┐           │  chat gets the full width
//   │▤ │  │ assistant │           │
//   │⌘ │  └───────────┘           │
//   │＋│         ┌────────┐       │
//   │  │         │  user  │       │
//   │  │  ┌ workflow steps ─────┐ │
//   │★ │  [ / or type…      ][⏺] │
//   └──┴──────────────────────────┘ 480
//
// The 34px rail carries streak and workflows so the conversation keeps full width —
// that is the whole point of this layout versus 3a, where both live in horizontal
// bands above and below the chat.
//
// Deviations from the 1c mock, both deliberate:
//   · A 22px BB sits at the top of the rail. 1c predates the mascot (turn 1), but BB
//     now owns the state machine, so his expression has to be visible somewhere.
//   · The 30px title bar is inherited from 3a. 1c was drawn as a static sidebar and
//     never had to solve dragging, edge-snapping or collapsing; the web build does.
// ==========================================

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  BbState,
  BuddyMessage,
  BuddyProgress,
  Density,
  Workflow,
  WorkflowRun,
} from '../models/buddy.types';
import { ACCENT_HEX, ACCENT_INK, MIC_SILENCE_MS, TRACK_HEX } from '../models/buddy.constants';
import { COMPANION_NAME, MOCK_TRANSCRIPT } from '../services/buddy-mock.data';
import { BbStateService } from '../services/bb-state.service';
import { BuddyChatService } from '../services/buddy-chat.service';
import { BuddyProgressService } from '../services/buddy-progress.service';
import { BuddyStateService } from '../services/buddy-state.service';
import { BuddyWorkflowService } from '../services/buddy-workflow.service';
import { BuddyChatLogComponent } from '../components/buddy-chat-log/buddy-chat-log.component';
import { BuddyInputRowComponent } from '../components/buddy-input-row/buddy-input-row.component';

const TRANSCRIPT_WORD_MS = 180;
/** Long-press on a rail icon opens parameters instead of running. */
const HOLD_MS = 400;

@Component({
  selector: 'app-buddy-rail',
  standalone: false,
  templateUrl: './buddy-rail.component.html',
  styleUrls: ['./buddy-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyRailComponent implements OnInit, OnDestroy {
  @Input() density: Density = 'default';

  @Output() dragStarted = new EventEmitter<PointerEvent>();
  @Output() collapseRequested = new EventEmitter<void>();
  @Output() edgeToggleRequested = new EventEmitter<void>();
  @Output() layoutToggleRequested = new EventEmitter<void>();

  @ViewChild(BuddyChatLogComponent) chatLog?: BuddyChatLogComponent;
  @ViewChild(BuddyInputRowComponent) inputRow?: BuddyInputRowComponent;

  readonly companionName = COMPANION_NAME;
  readonly streakColor = ACCENT_HEX.streak;
  readonly trackColor = TRACK_HEX;

  messages: readonly BuddyMessage[] = [];
  runs: Record<string, WorkflowRun> = {};
  progress!: BuddyProgress;
  bbState: BbState = 'idle';
  ttsEnabled = true;
  streaming = false;
  paletteOpen = false;
  habitatOpen = false;
  historyOpen = false;
  useEdgeTab = false;
  questVisible = true;

  micActive = false;
  handsFree = false;
  transcript = '';
  paletteQuery = '';

  private readonly destroyed$ = new Subject<void>();
  private transcriptTimer: ReturnType<typeof setTimeout> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private didHold = false;
  private stashedScroll = 0;

  constructor(
    private readonly chat: BuddyChatService,
    private readonly workflows: BuddyWorkflowService,
    private readonly progressService: BuddyProgressService,
    private readonly shell: BuddyStateService,
    private readonly bb: BbStateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.progress = this.progressService.current;
  }

  ngOnInit(): void {
    this.chat.messages.pipe(takeUntil(this.destroyed$)).subscribe((m) => {
      this.messages = m;
      this.cdr.markForCheck();
    });
    this.chat.isStreaming.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.streaming = s;
      this.cdr.markForCheck();
    });
    this.workflows.runs.pipe(takeUntil(this.destroyed$)).subscribe((r) => {
      this.runs = r;
      this.cdr.markForCheck();
    });
    // Re-render the rail + palette once the workflows load from the backend.
    this.workflows.workflows$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.cdr.markForCheck());
    this.progressService.progress.pipe(takeUntil(this.destroyed$)).subscribe((p) => {
      this.progress = p;
      this.cdr.markForCheck();
    });
    this.bb.state.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.bbState = s;
      this.cdr.markForCheck();
    });
    this.shell.ttsEnabled.pipe(takeUntil(this.destroyed$)).subscribe((t) => {
      this.ttsEnabled = t;
      this.cdr.markForCheck();
    });
    this.shell.paletteOpen.pipe(takeUntil(this.destroyed$)).subscribe((o) => {
      this.paletteOpen = o;
      this.cdr.markForCheck();
    });
    this.shell.historyOpen.pipe(takeUntil(this.destroyed$)).subscribe((o) => {
      this.historyOpen = o;
      this.cdr.markForCheck();
    });
    this.shell.useEdgeTab.pipe(takeUntil(this.destroyed$)).subscribe((v) => {
      this.useEdgeTab = v;
      this.cdr.markForCheck();
    });
    this.shell.questVisible.pipe(takeUntil(this.destroyed$)).subscribe((v) => {
      this.questVisible = v;
      this.cdr.markForCheck();
    });
    this.shell.habitatOpen.pipe(takeUntil(this.destroyed$)).subscribe((o) => {
      this.onHabitatToggled(o);
    });
  }

  // ------------------------------------------
  // Derived view state
  // ------------------------------------------

  /** The rail's streak ring doubles as the XP meter (1c). */
  get streakTurns(): number {
    if (!this.progress.xpForLevel) {
      return 0;
    }
    return Math.min(1, this.progress.xpIntoLevel / this.progress.xpForLevel);
  }

  get ringBackground(): string {
    return `conic-gradient(${this.streakColor} 0turn ${this.streakTurns}turn, ${this.trackColor} ${this.streakTurns}turn 1turn)`;
  }

  get questPct(): number {
    const q = this.progress.quest;
    return q.total ? Math.round((q.done / q.total) * 100) : 0;
  }

  get railWorkflows(): readonly Workflow[] {
    return this.workflows.railWorkflows;
  }

  get paletteWorkflows(): readonly Workflow[] {
    return this.workflows.filter(this.paletteQuery);
  }

  get speakingMessageId(): string | null {
    if (this.bbState !== 'speaking') {
      return null;
    }
    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      if (this.messages[i].role === 'assistant') {
        return this.messages[i].id;
      }
    }
    return null;
  }

  glyphColor(workflow: Workflow): string {
    return ACCENT_INK[workflow.accent];
  }

  trackWorkflow(_: number, workflow: Workflow): string {
    return workflow.id;
  }

  // ------------------------------------------
  // Rail actions
  // ------------------------------------------

  onIconDown(workflow: Workflow): void {
    this.didHold = false;
    this.clearHold();
    this.holdTimer = setTimeout(() => {
      this.didHold = true;
      this.holdTimer = null;
      this.onWorkflowParams(workflow.id);
    }, HOLD_MS);
  }

  onIconUp(workflow: Workflow): void {
    this.clearHold();
    if (!this.didHold) {
      this.onRunWorkflow(workflow.id);
    }
    this.didHold = false;
  }

  onIconCancel(): void {
    this.clearHold();
    this.didHold = false;
  }

  /** 1d: the sidebar's quest line is dismissible for the day. */
  dismissQuest(): void {
    this.shell.dismissQuestForToday();
  }

  // ------------------------------------------
  // Chat — same contract as the 3a panel
  // ------------------------------------------

  onSubmit(text: string): void {
    if (text.startsWith('/')) {
      this.shell.setPaletteOpen(true);
      return;
    }
    this.shell.setPaletteOpen(false);
    this.paletteQuery = '';
    this.chat.send(text);
  }

  onQueryChanged(value: string): void {
    if (value.startsWith('/')) {
      this.paletteQuery = value;
      this.shell.setPaletteOpen(true);
    } else if (this.paletteQuery) {
      this.paletteQuery = '';
      this.shell.setPaletteOpen(false);
    }
    this.bb.noteActivity();
  }

  onStopStream(): void {
    this.chat.stop();
  }

  onSuggestion(label: string): void {
    this.chat.send(label);
  }

  // ------------------------------------------
  // Voice
  // ------------------------------------------

  onMicToggled(): void {
    if (this.micActive) {
      this.commitTranscript();
    } else {
      this.startListening(false);
    }
  }

  onMicHeld(): void {
    this.startListening(true);
  }

  onTtsToggled(): void {
    const on = this.shell.toggleTts();
    if (!on && this.bbState === 'speaking') {
      this.bb.settle();
    }
  }

  onStopTts(): void {
    this.bb.settle();
  }

  private startListening(handsFree: boolean): void {
    this.micActive = true;
    this.handsFree = handsFree;
    this.transcript = '';
    this.bb.wake();
    this.bb.listen();

    const words = MOCK_TRANSCRIPT.split(' ');
    let index = 0;
    const tick = (): void => {
      if (index >= words.length) {
        this.transcriptTimer = null;
        if (handsFree) {
          this.silenceTimer = setTimeout(() => this.commitTranscript(), MIC_SILENCE_MS);
        }
        this.cdr.markForCheck();
        return;
      }
      this.transcript = words.slice(0, index + 1).join(' ');
      index += 1;
      this.cdr.markForCheck();
      this.transcriptTimer = setTimeout(tick, TRANSCRIPT_WORD_MS);
    };
    this.transcriptTimer = setTimeout(tick, TRANSCRIPT_WORD_MS);
  }

  private commitTranscript(): void {
    this.clearVoiceTimers();
    const text = this.transcript.trim();
    this.micActive = false;
    this.handsFree = false;
    this.transcript = '';
    this.cdr.markForCheck();

    if (text) {
      this.chat.send(text);
    } else {
      this.bb.settle();
    }
  }

  // ------------------------------------------
  // Workflows
  // ------------------------------------------

  onRunWorkflow(workflowId: string): void {
    this.shell.setPaletteOpen(false);
    this.paletteQuery = '';
    this.inputRow?.setValue('');
    this.workflows.run(workflowId);
  }

  onWorkflowParams(workflowId: string): void {
    const workflow = this.workflows.byId(workflowId);
    if (workflow) {
      this.inputRow?.setValue(`/${workflow.name} `);
      this.inputRow?.focus();
    }
  }

  onCancelRun(runId: string): void {
    this.workflows.cancel(runId);
  }

  onRetryRun(runId: string): void {
    this.workflows.retry(runId);
  }

  onDecideRun(payload: { runId: string; approved: boolean }): void {
    this.workflows.decide(payload.runId, payload.approved);
  }

  onDefineWorkflow(): void {
    this.shell.setPaletteOpen(false);
    const award = this.progressService.awardDefineWorkflow();
    this.chat.appendSystem('Recording your next exchange as a reusable workflow.');
    if (award.leveledUp) {
      this.bb.celebrate();
      this.chat.appendMilestone(`Level ${award.newLevel} — BB grows into it.`);
    } else {
      this.bb.succeed();
    }
  }

  onTogglePalette(): void {
    this.shell.togglePalette();
  }

  onToggleHistory(): void {
    this.shell.toggleHistory();
  }

  onCloseHistory(): void {
    this.shell.setHistoryOpen(false);
  }

  onToggleCollapseStyle(): void {
    this.shell.toggleCollapseStyle();
  }

  get collapseStyleLabel(): string {
    return this.useEdgeTab ? 'Collapse to a corner bubble instead' : 'Collapse to an edge tab instead';
  }

  onClosePalette(): void {
    this.shell.setPaletteOpen(false);
    this.paletteQuery = '';
  }

  // ------------------------------------------
  // Habitat
  // ------------------------------------------

  onOpenHabitat(): void {
    this.shell.openHabitat();
  }

  onCloseHabitat(): void {
    this.shell.closeHabitat();
  }

  private onHabitatToggled(open: boolean): void {
    if (open && !this.habitatOpen) {
      this.stashedScroll = this.chatLog?.scrollTop ?? 0;
    }
    const wasOpen = this.habitatOpen;
    this.habitatOpen = open;
    this.cdr.markForCheck();

    if (wasOpen && !open) {
      setTimeout(() => this.chatLog?.restoreScroll(this.stashedScroll));
    }
  }

  // ------------------------------------------
  // Chrome
  // ------------------------------------------

  onTitleBarPointerDown(event: PointerEvent): void {
    this.dragStarted.emit(event);
  }

  private clearHold(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  private clearVoiceTimers(): void {
    if (this.transcriptTimer !== null) {
      clearTimeout(this.transcriptTimer);
      this.transcriptTimer = null;
    }
    if (this.silenceTimer !== null) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearHold();
    this.clearVoiceTimers();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
